import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
    useDealerStep,
    useOSBStep,
    useVehicleStep,
} from '../../../../../hooks/owners-osb';
import useMarketDealerInfoService from '../../../../../hooks/owners-osb/use-market-dealerInfo-service';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import {
    AllOsbDealersResponse,
    DealerProfileInfo,
} from '../../../../../models/osb-model/osb-dealer-info';

import PreferredServiceCentre from './preferred-service-centre/preferred-service-centre';
import SearchServiceCentre from './search-service-centre/search-service-centre';
import PreferredServiceCentreSkeleton from './preferred-service-centre-skeleton/preferred-service-centre-skeleton';
import ErrorDisplay from '../../common/error-display/error-display';
import BookingLookupSection from '../../booking-lookup-section/booking-lookup-section';

export const ServiceCentreSelection = () => {
    const { osbVehicleStep } = useVehicleStep();
    const { setOSBStepPayload } = useOSBStep();
    const { setOSBDealerStepPayload } = useDealerStep();
    const { fetchMarketDealerInfo } = useMarketDealerInfoService();
    const deepLinkParams = new URLSearchParams(useLocation().search);
    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'service-centre-step') || {};
    const osbConfigData =
        useOSBContentFragment('common/osb-v3', 'configuration') || {};

    const [dealerInfo, setDealerInfo] = useState<AllOsbDealersResponse | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showPreferred, setShowPreferred] = useState(true);

    useEffect(() => {
        const fetchDealers = async () => {
            try {
                const response = await fetchMarketDealerInfo(deepLinkParams);
                if (response) {
                    setDealerInfo(response);
                    setError(null);
                } else {
                    setDealerInfo(null);
                    setError(
                        osbContentData?.serviceCentreErrorMessage as string
                    );
                }
            } catch (error) {
                console.error('Error fetching market dealer info:', error);
                setError(osbContentData?.serviceCentreErrorMessage as string);
            } finally {
                setLoading(false);
            }
        };

        fetchDealers();
    }, []);

    const preferredDealerCode = osbVehicleStep?.vehicleDetail?.preferredDealer;
    const dealerCodePrefixLength =
        parseInt(String(osbConfigData?.dealerCodePrefixLength), 10) || 0;
    const trimmedPreferredDealerCode = preferredDealerCode
        ? preferredDealerCode.slice(dealerCodePrefixLength)
        : null;
    const preferredDealer = dealerInfo?.dealers.find(
        dealer => dealer.dealerCode === trimmedPreferredDealerCode
    );

    const handleSearchDealerClick = () => {
        setShowPreferred(false);
    };

    const handlePreferredDealerClick = () => {
        setShowPreferred(true);
    };

    const handleSelectDealer = (selectedDealerProfile: DealerProfileInfo) => {
        setOSBStepPayload({
            preferredDealerJourney: true,
        });
        setOSBDealerStepPayload({
            selectedDealerProfile: selectedDealerProfile,
        });
    };

    const renderContent = () => {
        if (loading) {
            return <PreferredServiceCentreSkeleton />;
        }

        if (error) {
            return (
                <ErrorDisplay
                    title={`${osbContentData?.serviceCentreErrorTitle}`}
                    errorMessage={error}
                    suggestion={`${osbContentData?.serviceCentreErrorSuggestion}`}
                />
            );
        }

        if (showPreferred && preferredDealer) {
            return (
                <PreferredServiceCentre
                    preferredServiceCentre={preferredDealer}
                    onSearchDealerClick={handleSearchDealerClick}
                    onSelectPreferredDealerClick={handleSelectDealer}
                />
            );
        }

        return (
            <SearchServiceCentre
                onPreferredDealerClick={handlePreferredDealerClick}
                hasPreferredDealer={!!preferredDealer}
            />
        );
    };

    return (
        <>
            <div
                data-testid="service-centre-handler"
                className="osb-v3-service-handler"
            >
                {renderContent()}
            </div>
            <BookingLookupSection className="booking-lookup-offset--visual" />
        </>
    );
};
